import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Swords, Trophy, Users, Calendar, Plus, TrendingUp, Mail } from "lucide-react";
import { PageHeader, Loader, btn } from "@/components/Page";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function ArenaCircuitDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [season, setSeason] = useState(null);
  const [myFighters, setMyFighters] = useState([]);
  const [slotsUsed, setSlotsUsed] = useState(0);
  const [slotsTotal, setSlotsTotal] = useState(1);
  const [pendingOffers, setPendingOffers] = useState(0);
  const [pendingFightOffers, setPendingFightOffers] = useState(0);
  const [hasCircuit, setHasCircuit] = useState(false);

  useEffect(() => {
    loadDashboard();
    loadOffers();
    loadFightOffers();
    checkCircuit();
  }, []);

  const loadDashboard = async () => {
    try {
      const { data } = await api.get("/arena/seasons/current");
      setSeason(data.season);
      setMyFighters(data.my_fighters);
      setSlotsUsed(data.slots_used);
      setSlotsTotal(data.slots_total);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  const loadOffers = async () => {
    try {
      const { data } = await api.get("/arena/contracts/offers");
      setPendingOffers(data.offers.length);
    } catch (e) {
      // Silently fail
    }
  };

  const loadFightOffers = async () => {
    try {
      const { data } = await api.get("/arena-circuit/fight-offers");
      // Count only pending offers
      const pending = (data.offers || []).filter(o => o.status === "pending");
      setPendingFightOffers(pending.length);
    } catch (e) {
      // Silently fail
    }
  };

  const checkCircuit = async () => {
    try {
      const { data } = await api.get("/arena/circuits/my-circuit");
      setHasCircuit(!!data.circuit);
    } catch (e) {
      // Silently fail
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="space-y-8">
      <PageHeader
        icon={Swords}
        title="The Arena Circuit"
        subtitle="Seasonal gladiatorial competition"
      />

      {/* Season Banner */}
      <div className="bg-gradient-to-r from-amber-950/40 to-red-950/40 border border-amber-700/40 rounded-lg p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-cinzel text-2xl text-amber-300 tracking-wide mb-2">
              {season?.name || "Season 1"}
            </h2>
            <p className="text-sm text-zinc-400 mb-4">
              Compete in editor-run circuits, train your gladiators, and claim the Corona Aurea
            </p>
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Users size={16} className="text-amber-400" />
                <span className="text-zinc-300">
                  <span className="text-amber-400 font-semibold">{season?.stats?.total_fighters || 0}</span> Active Gladiators
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Trophy size={16} className="text-amber-400" />
                <span className="text-zinc-300">
                  <span className="text-amber-400 font-semibold">{season?.stats?.total_circuits || 0}</span> Active Circuits
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Swords size={16} className="text-amber-400" />
                <span className="text-zinc-300">
                  <span className="text-amber-400 font-semibold">{season?.stats?.total_bouts || 0}</span> Total Bouts
                </span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Season Ends In</div>
            <div className="text-sm text-amber-300 font-cinzel">
              {season?.end_date ? (() => {
                const end = new Date(season.end_date);
                const now = new Date();
                const diff = end - now;
                const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                if (days > 0) return `${days} days`;
                const hours = Math.floor(diff / (1000 * 60 * 60));
                if (hours > 0) return `${hours} hours`;
                return "Soon";
              })() : "Ongoing"}
            </div>
          </div>
        </div>
      </div>

      {/* Contract Offers Notification */}
      {pendingOffers > 0 && (
        <div
          onClick={() => navigate("/arena-circuit/contract-offers")}
          className="bg-purple-950/20 border border-purple-700/40 rounded-lg p-4 cursor-pointer hover:border-purple-600/60 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <Mail size={24} className="text-purple-400" />
              <span className="absolute -top-1 -right-1 bg-purple-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {pendingOffers}
              </span>
            </div>
            <div>
              <h4 className="font-cinzel text-purple-300 font-semibold">New Contract Offer{pendingOffers !== 1 ? "s" : ""}!</h4>
              <p className="text-sm text-zinc-400">
                You have {pendingOffers} pending contract offer{pendingOffers !== 1 ? "s" : ""} for your gladiators
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Fight Offers Notification */}
      {pendingFightOffers > 0 && (
        <div
          onClick={() => navigate("/arena-circuit/fight-offers")}
          className="bg-red-950/20 border border-red-700/40 rounded-lg p-4 cursor-pointer hover:border-red-600/60 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <Swords size={24} className="text-red-400" />
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {pendingFightOffers}
              </span>
            </div>
            <div>
              <h4 className="font-cinzel text-red-300 font-semibold">New Fight Offer{pendingFightOffers !== 1 ? "s" : ""}!</h4>
              <p className="text-sm text-zinc-400">
                You have {pendingFightOffers} pending fight offer{pendingFightOffers !== 1 ? "s" : ""} to review
              </p>
            </div>
          </div>
        </div>
      )}

      {/* My Gladiators Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-cinzel text-xl text-amber-300 uppercase tracking-wide">
            My Gladiators
          </h3>
          <div className="flex items-center gap-3">
            <span className="text-sm text-zinc-400">
              <span className="text-amber-400 font-semibold">{slotsUsed}</span> / {slotsTotal} slots
            </span>
            {slotsUsed < slotsTotal && (
              <button
                onClick={() => navigate("/arena-circuit/create-fighter")}
                className={btn() + " flex items-center gap-2"}
              >
                <Plus size={16} />
                Create Gladiator
              </button>
            )}
          </div>
        </div>

        {myFighters.length === 0 ? (
          <div className="border border-amber-900/30 bg-zinc-950 p-8 text-center">
            <Swords size={48} className="text-amber-700/50 mx-auto mb-4" />
            <h4 className="font-cinzel text-lg text-zinc-300 mb-2">No Fighters Yet</h4>
            <p className="text-sm text-zinc-500 mb-4">
              Create your first Arena Fighter to begin competing in circuits
            </p>
            <button
              onClick={() => navigate("/arena-circuit/create-fighter")}
              className={btn()}
            >
              <Plus size={16} className="inline mr-2" />
              Create Your First Fighter
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {myFighters.map((fighter) => (
              <div
                key={fighter.id}
                onClick={() => navigate(`/arena-circuit/fighter/${fighter.id}`)}
                className="border border-amber-900/30 bg-zinc-950 p-4 hover:border-amber-600/50 transition-colors cursor-pointer"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-cinzel text-lg text-amber-300 tracking-wide">
                      {fighter.name}
                    </h4>
                    <p className="text-xs text-zinc-500 capitalize">{fighter.cls}</p>
                  </div>
                  {fighter.corona_holder && (
                    <Trophy size={20} className="text-amber-400" />
                  )}
                </div>

                <div className="flex items-center gap-4 text-xs text-zinc-400 mb-3">
                  <span className="flex items-center gap-1">
                    <Trophy size={12} />
                    {fighter.record.wins}W / {fighter.record.losses}L
                  </span>
                </div>

                {fighter.current_contract ? (
                  <div className="text-xs text-emerald-400 border-t border-zinc-800 pt-2">
                    ⚔️ Under Contract
                  </div>
                ) : (
                  <div className="text-xs text-zinc-500 border-t border-zinc-800 pt-2">
                    Free Agent
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          onClick={() => navigate("/arena-circuit/fighters")}
          className="border border-amber-900/30 bg-zinc-950 p-6 hover:border-amber-600/50 transition-colors text-left"
        >
          <Swords size={24} className="text-amber-400 mb-3" />
          <h4 className="font-cinzel text-amber-300 text-lg mb-1">My Gladiators</h4>
          <p className="text-sm text-zinc-500">View and manage your arena gladiators</p>
        </button>

        <button
          onClick={() => navigate("/arena-circuit/training")}
          className="border border-amber-900/30 bg-zinc-950 p-6 hover:border-amber-600/50 transition-colors text-left"
        >
          <TrendingUp size={24} className="text-amber-400 mb-3" />
          <h4 className="font-cinzel text-amber-300 text-lg mb-1">Training</h4>
          <p className="text-sm text-zinc-500">Set training schedules for your gladiators</p>
        </button>

        <button
          onClick={() => navigate("/arena-circuit/circuits")}
          className="border border-amber-900/30 bg-zinc-950 p-6 hover:border-amber-600/50 transition-colors text-left"
        >
          <Trophy size={24} className="text-amber-400 mb-3" />
          <h4 className="font-cinzel text-amber-300 text-lg mb-1">Browse Circuits</h4>
          <p className="text-sm text-zinc-500">Find circuits to join and compete</p>
        </button>
      </div>

      {/* VIP Editor Notice */}
      {user?.vip && user?.vip_status === "ACTIVE" && !hasCircuit && (
        <div className="bg-purple-950/20 border border-purple-700/40 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Trophy size={20} className="text-purple-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-cinzel text-purple-300 font-semibold mb-1">VIP Editor Access</h4>
              <p className="text-sm text-zinc-400 mb-2">
                As a VIP member, you can create your own Arena Circuit and organize fight cards
              </p>
              <button
                onClick={() => navigate("/arena-circuit/create-circuit")}
                className={btn() + " text-sm"}
              >
                Create Circuit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* My Circuit Quick Access (if editor has circuit) */}
      {hasCircuit && (
        <div className="bg-purple-950/20 border border-purple-700/40 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Trophy size={20} className="text-purple-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-cinzel text-purple-300 font-semibold mb-1">Your Circuit</h4>
              <p className="text-sm text-zinc-400 mb-2">
                Manage your circuit, recruit gladiators, and organize events
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => navigate("/arena-circuit/my-circuit")}
                  className={btn() + " text-sm"}
                >
                  Manage Circuit
                </button>
                <button
                  onClick={() => navigate("/arena-circuit/recruit")}
                  className={btn() + " text-sm bg-zinc-800 hover:bg-zinc-700"}
                >
                  Recruit Gladiators
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
